/**
 * SaaS Service API Client
 *
 * Handles communication with the SaaS backend for:
 * - Multi-step registration flow
 * - Subscription management
 * - Organization provisioning
 *
 * Base URL: http://localhost:8002 (development)
 */

import axios from 'axios';

// SaaS Service base URL (different from main backend)
const SAAS_API_BASE_URL = process.env.REACT_APP_SAAS_API_URL || 'http://localhost:8002';

// Create axios instance for SaaS service
const saasApiClient = axios.create({
  baseURL: SAAS_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add response interceptor for error handling
saasApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const detail = error.response?.data?.detail;
    let message = error.message || 'Une erreur est survenue';
    let fieldErrors = null;

    if (Array.isArray(detail)) {
      fieldErrors = {};
      detail.forEach((item) => {
        const loc = item?.loc || [];
        const field = loc.includes('body') ? loc[loc.length - 1] : loc[1];
        if (field) {
          fieldErrors[field] = item?.msg || message;
        }
      });
      message = detail[0]?.msg || message;
    } else if (typeof detail === 'string') {
      message = detail;
    }

    console.error('SaaS API Error:', detail || message);
    return Promise.reject({ message, fieldErrors });
  }
);

/**
 * Registration Flow APIs
 */
export const registrationApi = {
  /**
   * Step 1: Company Information
   * @param {Object} data - Company details
   * @returns {Promise<{subdomain: string, subdomain_available: boolean, session_id: string}>}
   */
  step1: async (data) => {
    const response = await saasApiClient.post('/api/v1/register/step1', data);
    return response.data;
  },

  /**
   * Step 2: Admin Contact
   * @param {string} sessionId - Session ID from step 1
   * @param {Object} data - Admin contact details
   * @returns {Promise<{email_available: boolean, session_id: string}>}
   */
  step2: async (sessionId, data) => {
    const response = await saasApiClient.post(
      `/api/v1/register/step2?session_id=${sessionId}`,
      data
    );
    return response.data;
  },

  /**
   * Step 3: Plan Selection
   * @param {string} sessionId - Session ID
   * @param {Object} data - {plan_code, billing_cycle}
   * @returns {Promise<Object>} Plan details with pricing
   */
  step3: async (sessionId, data) => {
    const response = await saasApiClient.post(
      `/api/v1/register/step3?session_id=${sessionId}`,
      data
    );
    return response.data;
  },

  /**
   * Step 4: Payment (optional for trial)
   * @param {string} sessionId - Session ID
   * @param {Object} data - {skip_payment, payment_method, payment_gateway}
   * @returns {Promise<Object>} Payment confirmation or trial details
   */
  step4: async (sessionId, data) => {
    const response = await saasApiClient.post(
      `/api/v1/register/step4?session_id=${sessionId}`,
      data
    );
    return response.data;
  },

  /**
   * Step 5: Finalize Registration
   * Creates organization, provisions tenant database, sends welcome email
   * @param {string} sessionId - Session ID
   * @returns {Promise<{subdomain: string, login_url: string, trial_end_date: string}>}
   */
  step5: async (sessionId) => {
    const response = await saasApiClient.post(
      `/api/v1/register/step5?session_id=${sessionId}`
    );
    return response.data;
  },

  /**
   * Initiate a Stripe Checkout Session for an existing organization
   * @param {Object} data - {subdomain, plan_code, billing_cycle, admin_email, ...}
   * @returns {Promise<{session_id: string, checkout_url: string, amount: number, currency: string}>}
   */
  initiateStripePayment: async (data) => {
    const response = await saasApiClient.post('/api/v1/payments/stripe/initiate', data);
    return response.data;
  },

  /**
   * Get Stripe Checkout Session status by session_id
   * @param {string} sessionId
   * @returns {Promise<{session_id, payment_status, status, amount_total, currency}>}
   */
  getStripeSessionStatus: async (sessionId) => {
    const response = await saasApiClient.get(`/api/v1/payments/stripe/status/${sessionId}`);
    return response.data;
  },

  /**
   * Resend welcome email with new credentials
   * @param {string} email - Admin email address
   * @param {string} subdomain - Organization subdomain
   * @returns {Promise<{message: string}>}
   */
  resendWelcomeEmail: async (email, subdomain) => {
    const response = await saasApiClient.post('/api/v1/register/resend-welcome', { email, subdomain });
    return response.data;
  },

  /**
   * Poll the provisioning status after step 5.
   * @param {number} organizationId - From the step5 response
   * @param {string} subdomain - From the step5 response
   * @returns {Promise<{status: 'provisioning'|'ready'|'failed'}>}
   */
  getProvisioningStatus: async (organizationId, subdomain) => {
    const response = await saasApiClient.get(
      `/api/v1/register/status/${organizationId}?subdomain=${encodeURIComponent(subdomain)}`
    );
    return response.data;
  },
};

/**
 * Subscription Management APIs
 */
export const subscriptionApi = {
  /**
   * Get current subscription details
   * @returns {Promise<Object>}
   */
  getCurrent: async () => {
    const response = await saasApiClient.get('/api/v1/subscriptions/current');
    return response.data;
  },

  /**
   * Upgrade to higher plan
   * @param {string} newPlanCode - Target plan code
   * @returns {Promise<Object>}
   */
  upgrade: async (newPlanCode) => {
    const response = await saasApiClient.post('/api/v1/subscriptions/upgrade', {
      new_plan_code: newPlanCode,
    });
    return response.data;
  },

  /**
   * Downgrade to lower plan
   * @param {string} newPlanCode - Target plan code
   * @returns {Promise<Object>}
   */
  downgrade: async (newPlanCode) => {
    const response = await saasApiClient.post('/api/v1/subscriptions/downgrade', {
      new_plan_code: newPlanCode,
    });
    return response.data;
  },

  /**
   * Cancel subscription
   * @param {string} reason - Cancellation reason
   * @returns {Promise<Object>}
   */
  cancel: async (reason) => {
    const response = await saasApiClient.post('/api/v1/subscriptions/cancel', {
      reason,
    });
    return response.data;
  },
};

/**
 * Health check
 */
export const healthCheck = async () => {
  const response = await saasApiClient.get('/health');
  return response.data;
};

export default saasApiClient;
